'use client';

import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Whether the results screen shows its extraction panel beside the document,
 * or gives the document the full width.
 *
 * OMA-624's "toggle so I can work faster without it" is about THIS panel, not
 * the app's nav sidebar — the sidebar already has a trigger in the app header,
 * so a second control for it on this screen was redundant. What needs the room
 * is the document: a dense Arabic scan at a legible zoom does not fit beside a
 * 420px panel.
 *
 * Defaults to VISIBLE, the opposite of the nav sidebar's default. The
 * extracted values are the point of the screen; opening it with them hidden
 * would read as a failed extraction. Collapsing is an explicit choice and is
 * honoured in both directions on the next visit.
 *
 * Persisted through Zustand with `skipHydration`, which is this repo's
 * established answer for a stored UI preference (`use-panel-width.ts`,
 * `use-sidebar-groups.ts`). Reading `localStorage` in an effect and calling
 * local `setState` with the result is the obvious alternative and is REJECTED
 * by the React Compiler ("Calling setState synchronously within an effect can
 * trigger cascading renders"); reading it during render instead would resolve
 * to the default on the server and to the stored value on the client, which is
 * a hydration mismatch. Rehydrating a store is neither.
 */
export const RESULTS_PANEL_STORAGE_KEY = 'ocr-results-panel';

type ResultsPanelState = {
  visible: boolean;
  toggle: () => void;
};

export const useResultsPanelStore = create<ResultsPanelState>()(
  persist(
    (set) => ({
      visible: true,
      toggle: () => set((state) => ({ visible: !state.visible }))
    }),
    { name: RESULTS_PANEL_STORAGE_KEY, skipHydration: true }
  )
);

export function useResultsPanel(): { visible: boolean; toggle: () => void } {
  const visible = useResultsPanelStore((state) => state.visible);
  const toggle = useResultsPanelStore((state) => state.toggle);

  useEffect(() => {
    void useResultsPanelStore.persist.rehydrate();
  }, []);

  return { visible, toggle };
}
